/** Floor when viewport math is unavailable (SSR / first paint). */
export const EVIMO_TABLE_MIN_BODY_SCROLL_Y = 320;
export const EVIMO_TABLE_SCROLL_BOTTOM_GAP_PX = 8;
export const EVIMO_TABLE_PAGINATION_FALLBACK_HEIGHT_PX = 48;
export const EVIMO_TABLE_HEADER_FALLBACK_HEIGHT_PX = 39;

export type ComputeEvimoTableBodyScrollYOptions = {
  minBodyHeight?: number;
  bottomGap?: number;
  footerSelector?: string;
  getViewportBottom?: (wrap: HTMLElement) => number;
};

export function defaultEvimoTableViewportBottom(wrap: HTMLElement): number {
  const modalBody = wrap.closest('.ant-modal-body');
  if (modalBody instanceof HTMLElement) {
    return modalBody.getBoundingClientRect().bottom;
  }

  const drawerBody = wrap.closest('.ant-drawer-body');
  if (drawerBody instanceof HTMLElement) {
    return drawerBody.getBoundingClientRect().bottom;
  }

  const footer = document.querySelector('.app-site-footer');
  if (footer instanceof HTMLElement) {
    return footer.getBoundingClientRect().top;
  }

  return window.innerHeight;
}

function measureElementHeight(
  root: HTMLElement,
  selector: string,
  fallback: number,
): number {
  const el = root.querySelector(selector);
  if (el instanceof HTMLElement) {
    const height = el.getBoundingClientRect().height;
    if (height > 0) {
      return height;
    }
  }
  return fallback;
}

/**
 * Height for Ant Design Table `scroll.y` so the tbody scrolls while header + pagination stay visible.
 */
export function computeEvimoTableBodyScrollY(
  wrap: HTMLElement,
  options?: ComputeEvimoTableBodyScrollYOptions,
): number {
  const minBody = options?.minBodyHeight ?? EVIMO_TABLE_MIN_BODY_SCROLL_Y;
  const bottomGap = options?.bottomGap ?? EVIMO_TABLE_SCROLL_BOTTOM_GAP_PX;
  const viewportBottom = (
    options?.getViewportBottom ?? defaultEvimoTableViewportBottom
  )(wrap);

  const hasPagination =
    wrap.querySelector('.ant-pagination') instanceof HTMLElement;
  const paginationHeight = hasPagination
    ? measureElementHeight(
        wrap,
        '.ant-pagination',
        EVIMO_TABLE_PAGINATION_FALLBACK_HEIGHT_PX,
      )
    : measureElementHeight(wrap, '.ant-table-footer, .ant-table-summary', 0);

  const headerHeight = Math.max(
    measureElementHeight(wrap, '.ant-table-header', 0),
    measureElementHeight(wrap, '.ant-table-thead', 0),
    EVIMO_TABLE_HEADER_FALLBACK_HEIGHT_PX,
  );

  const wrapRect = wrap.getBoundingClientRect();
  const fromViewport =
    viewportBottom - wrapRect.top - paginationHeight - headerHeight - bottomGap;

  // Fill the page below the table — do not shrink to a short content-sized wrap.
  return Math.max(minBody, Math.floor(fromViewport));
}

/** Used until the table wrap is measured (avoids a tiny first paint). */
export function estimateEvimoTableBodyScrollYFallback(): number {
  if (typeof window === 'undefined') {
    return EVIMO_TABLE_MIN_BODY_SCROLL_Y;
  }

  const footer = document.querySelector('.app-site-footer');
  const viewportBottom =
    footer instanceof HTMLElement
      ? footer.getBoundingClientRect().top
      : window.innerHeight;

  const layoutRoot =
    document.querySelector('.landing-main-column') ?? document.documentElement;
  const offsetPx = readCssPx('--evimo-table-viewport-offset', 212, layoutRoot);
  const paginationHeight = EVIMO_TABLE_PAGINATION_FALLBACK_HEIGHT_PX;
  const headerHeight = EVIMO_TABLE_HEADER_FALLBACK_HEIGHT_PX;
  const bottomGap = EVIMO_TABLE_SCROLL_BOTTOM_GAP_PX;

  const fromViewport =
    viewportBottom - offsetPx - paginationHeight - headerHeight - bottomGap;

  return Math.max(EVIMO_TABLE_MIN_BODY_SCROLL_Y, Math.floor(fromViewport));
}

function readCssPx(
  variable: string,
  fallback: number,
  element: Element = document.documentElement,
): number {
  const raw = getComputedStyle(element).getPropertyValue(variable);
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}
